import React, { useState } from 'react';
import { cn, getSeverityColor, formatDate } from '@/lib/utils';
import { 
  AlertTriangle, ExternalLink, CheckCircle, Clock, Shield, 
  Zap, Target, FileText, Copy, ChevronRight, Lightbulb,
  Terminal, Globe, Server
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';

// Soluções sugeridas baseadas no tipo de alerta
const getSuggestedActions = (alert) => {
  const actions = {
    'ids': [
      { title: 'Bloquear IP de origem no firewall', command: `iptables -A INPUT -s ${alert.source_ip || 'IP'} -j DROP`, priority: 'high' },
      { title: 'Analisar logs do período', command: `grep "${alert.source_ip}" /var/log/syslog | tail -100`, priority: 'medium' },
      { title: 'Verificar se há outros hosts afetados', command: `netstat -an | grep ${alert.source_ip}`, priority: 'medium' },
      { title: 'Atualizar regras do IDS/IPS', command: 'suricata-update && systemctl restart suricata', priority: 'low' },
    ],
    'nac': [
      { title: 'Isolar dispositivo em VLAN de quarentena', command: `packetfence node isolate ${alert.asset_hostname || 'MAC'}`, priority: 'high' },
      { title: 'Verificar identidade do dispositivo', command: 'nmap -sV -O TARGET_IP', priority: 'medium' },
      { title: 'Revisar políticas de acesso', command: 'cat /etc/packetfence/policies.conf', priority: 'low' },
    ],
    'vulnerability': [
      { title: 'Aplicar patch de segurança', command: 'apt update && apt upgrade -y', priority: 'high' },
      { title: 'Verificar versão atual do software', command: 'dpkg -l | grep PACKAGE_NAME', priority: 'medium' },
      { title: 'Criar regra de mitigação temporária', command: 'See CVE details for specific mitigation', priority: 'medium' },
    ],
    'compliance': [
      { title: 'Revisar configuração de segurança', command: 'lynis audit system', priority: 'high' },
      { title: 'Gerar relatório de compliance', command: 'oscap xccdf eval --profile PROFILE', priority: 'medium' },
    ],
    'siem': [
      { title: 'Correlacionar eventos relacionados', command: `wazuh-logtest < /var/ossec/logs/alerts/alerts.json`, priority: 'high' },
      { title: 'Verificar integridade dos arquivos', command: 'wazuh-syscheckd -f', priority: 'medium' },
      { title: 'Analisar comportamento do usuário', command: 'aureport -au -ts today', priority: 'low' },
    ],
  };
  
  return actions[alert.source] || actions['ids'];
};

// MITRE ATT&CK info
const getMitreInfo = (technique) => {
  const mitre = {
    'T1190': { name: 'Exploit Public-Facing Application', tactic: 'Initial Access', description: 'Adversários podem tentar explorar vulnerabilidades em aplicações expostas à internet.' },
    'T1133': { name: 'External Remote Services', tactic: 'Initial Access', description: 'Adversários podem usar serviços remotos externos para acesso inicial.' },
    'T1078': { name: 'Valid Accounts', tactic: 'Defense Evasion', description: 'Adversários podem obter e usar credenciais válidas para acesso.' },
    'T1059': { name: 'Command and Scripting Interpreter', tactic: 'Execution', description: 'Adversários podem usar interpretadores de comando para executar scripts.' },
    'T1486': { name: 'Data Encrypted for Impact', tactic: 'Impact', description: 'Adversários podem criptografar dados para causar impacto (ransomware).' },
  };
  return mitre[technique] || { name: technique, tactic: 'Unknown', description: 'Técnica MITRE ATT&CK' };
};

export function CriticalAlerts({ alerts = [], onStatusChange }) {
  const [selectedAlert, setSelectedAlert] = useState(null);
  const criticalAlerts = alerts.filter(a => a.severity === 'critical' || a.severity === 'high');

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="space-y-4" data-testid="critical-alerts">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-sm uppercase tracking-wider text-muted-foreground">
          Alertas Críticos
        </h3>
        <Badge variant="destructive" className="font-mono">
          {criticalAlerts.length}
        </Badge>
      </div>
      
      <ScrollArea className="h-[390px]">
        <div className="space-y-2">
          {criticalAlerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <CheckCircle className="h-8 w-8 mb-2 text-success" />
              <span className="text-sm">Nenhum alerta crítico</span>
            </div>
          ) : (
            criticalAlerts.map((alert) => (
              <div
                key={alert.id}
                onClick={() => setSelectedAlert(alert)}
                className="bg-secondary/50 rounded-sm p-4 border border-border hover:border-destructive/50 transition-all group cursor-pointer"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className={cn(
                      "h-5 w-5 mt-0.5 flex-shrink-0",
                      alert.severity === 'critical' ? 'text-destructive animate-pulse' : 'text-warning'
                    )} />
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{alert.title}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">{alert.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className={cn("text-xs border", getSeverityColor(alert.severity))}>
                          {alert.severity.toUpperCase()}
                        </Badge>
                        <span className="text-xs text-muted-foreground font-mono">
                          {alert.source}
                        </span>
                        {alert.mitre_technique && (
                          <span className="text-xs text-info font-mono">
                            {alert.mitre_technique}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  <span>{formatDate(alert.created_at)}</span>
                  {alert.source_country && (
                    <>
                      <span>•</span>
                      <Globe className="h-3 w-3" />
                      <span>{alert.source_country}</span>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Alert Detail Modal */}
      <Dialog open={!!selectedAlert} onOpenChange={() => setSelectedAlert(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <AlertTriangle className={cn(
                "h-6 w-6",
                selectedAlert?.severity === 'critical' ? 'text-destructive' : 'text-warning'
              )} />
              <span>{selectedAlert?.title}</span>
            </DialogTitle>
          </DialogHeader>
          
          {selectedAlert && (
            <Tabs defaultValue="details" className="mt-4">
              <TabsList className="bg-secondary">
                <TabsTrigger value="details">Detalhes</TabsTrigger>
                <TabsTrigger value="actions">Ações Sugeridas</TabsTrigger>
                <TabsTrigger value="mitre">MITRE ATT&CK</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
              </TabsList>
              
              <ScrollArea className="h-[450px] mt-4">
                <TabsContent value="details" className="space-y-4 mt-0">
                  {/* Alert Info */}
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <p className="text-sm text-muted-foreground mb-4">{selectedAlert.description}</p>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-xs text-muted-foreground uppercase">Severidade</span>
                          <div className="mt-1">
                            <Badge className={cn("border", getSeverityColor(selectedAlert.severity))}>
                              {selectedAlert.severity.toUpperCase()}
                            </Badge>
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground uppercase">Status</span>
                          <div className="mt-1">
                            <Badge variant="outline">{selectedAlert.status}</Badge>
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground uppercase">Fonte</span>
                          <p className="font-mono text-sm mt-1">{selectedAlert.source}</p>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground uppercase">Criado em</span>
                          <p className="font-mono text-sm mt-1">{formatDate(selectedAlert.created_at)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Origin Info */}
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3 flex items-center gap-2">
                        <Globe className="h-4 w-4" /> Origem do Ataque
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-xs text-muted-foreground">IP de Origem</span>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="font-mono text-sm bg-background px-2 py-1 rounded">
                              {selectedAlert.source_ip || 'N/A'}
                            </code>
                            {selectedAlert.source_ip && (
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copyToClipboard(selectedAlert.source_ip)}>
                                <Copy className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">País</span>
                          <p className="font-mono text-sm mt-1">{selectedAlert.source_country || 'Desconhecido'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Coordenadas</span>
                          <p className="font-mono text-sm mt-1">
                            {selectedAlert.source_lat?.toFixed(4)}, {selectedAlert.source_lng?.toFixed(4)}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Asset Afetado</span>
                          <p className="font-mono text-sm mt-1">{selectedAlert.asset_hostname || 'N/A'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Threat Intel Match */}
                  {selectedAlert.threat_intel_match && (
                    <Card className="bg-destructive/10 border-destructive/30">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-destructive mb-3 flex items-center gap-2">
                          <Shield className="h-4 w-4" /> Match de Threat Intelligence
                        </h4>
                        <p className="text-sm">Este IP foi encontrado em bases de Threat Intelligence.</p>
                        <div className="mt-2">
                          <Badge variant="destructive">{selectedAlert.threat_classification}</Badge>
                          {selectedAlert.threat_names?.map((name, i) => (
                            <Badge key={i} variant="outline" className="ml-2">{name}</Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="actions" className="space-y-4 mt-0">
                  <Card className="bg-primary/10 border-primary/30">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-primary mb-2 flex items-center gap-2">
                        <Lightbulb className="h-4 w-4" /> Recomendação
                      </h4>
                      <p className="text-sm">
                        Baseado na análise deste alerta, recomendamos executar as ações abaixo em ordem de prioridade.
                      </p>
                    </CardContent>
                  </Card>

                  {getSuggestedActions(selectedAlert).map((action, i) => (
                    <Card key={i} className="bg-secondary/50">
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={cn(
                              "p-2 rounded",
                              action.priority === 'high' ? 'bg-destructive/20' :
                              action.priority === 'medium' ? 'bg-warning/20' : 'bg-info/20'
                            )}>
                              <Zap className={cn(
                                "h-4 w-4",
                                action.priority === 'high' ? 'text-destructive' :
                                action.priority === 'medium' ? 'text-warning' : 'text-info'
                              )} />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{action.title}</p>
                              <Badge variant="outline" className="mt-1 text-xs">
                                Prioridade: {action.priority}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <code className="flex-1 text-xs bg-background p-2 rounded font-mono overflow-x-auto">
                            {action.command}
                          </code>
                          <Button size="icon" variant="ghost" onClick={() => copyToClipboard(action.command)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  <div className="flex gap-2 pt-4">
                    <Button className="flex-1" onClick={() => onStatusChange?.(selectedAlert.id, 'investigating')}>
                      <Target className="h-4 w-4 mr-2" />
                      Iniciar Investigação
                    </Button>
                    <Button variant="outline" onClick={() => onStatusChange?.(selectedAlert.id, 'resolved')}>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Resolver
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="mitre" className="space-y-4 mt-0">
                  {selectedAlert.mitre_technique ? (
                    <>
                      <Card className="bg-secondary/50">
                        <CardContent className="pt-4">
                          <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">
                            Técnica MITRE ATT&CK
                          </h4>
                          <div className="space-y-3">
                            <div>
                              <span className="text-xs text-muted-foreground">ID</span>
                              <p className="font-mono text-lg text-info">{selectedAlert.mitre_technique}</p>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground">Nome</span>
                              <p className="font-medium">{getMitreInfo(selectedAlert.mitre_technique).name}</p>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground">Tática</span>
                              <Badge variant="outline" className="ml-2">
                                {getMitreInfo(selectedAlert.mitre_technique).tactic}
                              </Badge>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground">Descrição</span>
                              <p className="text-sm mt-1">{getMitreInfo(selectedAlert.mitre_technique).description}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Button variant="outline" className="w-full" asChild>
                        <a 
                          href={`https://attack.mitre.org/techniques/${selectedAlert.mitre_technique}/`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Ver no MITRE ATT&CK
                        </a>
                      </Button>
                    </>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Nenhuma técnica MITRE ATT&CK associada a este alerta.</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="timeline" className="space-y-4 mt-0">
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <div className="space-y-4">
                        <div className="flex gap-3">
                          <div className="w-2 h-2 rounded-full bg-destructive mt-2" />
                          <div>
                            <p className="font-medium text-sm">Alerta Criado</p>
                            <p className="text-xs text-muted-foreground">{formatDate(selectedAlert.created_at)}</p>
                            <p className="text-sm mt-1">Detecção automática pelo {selectedAlert.source.toUpperCase()}</p>
                          </div>
                        </div>
                        
                        {selectedAlert.status === 'investigating' && (
                          <div className="flex gap-3">
                            <div className="w-2 h-2 rounded-full bg-warning mt-2" />
                            <div>
                              <p className="font-medium text-sm">Em Investigação</p>
                              <p className="text-xs text-muted-foreground">Status atualizado</p>
                            </div>
                          </div>
                        )}
                        
                        {selectedAlert.status === 'resolved' && (
                          <div className="flex gap-3">
                            <div className="w-2 h-2 rounded-full bg-success mt-2" />
                            <div>
                              <p className="font-medium text-sm">Resolvido</p>
                              <p className="text-xs text-muted-foreground">{formatDate(selectedAlert.resolved_at)}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CriticalAlerts;
